import { watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { http } from '@/shared/api/http';
import { applyLocale } from '@/shared/i18n/i18n';
import type { CurrentUser } from '@/entities/user/model/types';

export const userKeys = {
  me: ['users', 'me'] as const
};

/**
 * L'identité, en dehors du cache de vue-query.
 *
 * Une garde de route s'exécute avant qu'aucun composant n'existe, donc avant qu'aucun
 * `useQuery` n'ait été monté : elle ne peut pas lire le cache, il lui faut la requête. Le
 * résultat est retenu ici parce que rien de ce qu'elle regarde — les organisations
 * administrées — ne change le temps d'un onglet, et qu'une navigation ne doit pas coûter un
 * aller-retour de plus.
 */
let identity: Promise<CurrentUser> | null = null;

/**
 * La connexion n'est horodatée qu'une fois par chargement de l'application.
 *
 * Et elle l'est **après** avoir lu l'identité, jamais avant : `onboarding.required` se
 * calcule sur `lastConnectionAt`, donc l'écrire d'abord effacerait la seule chose qui
 * distingue un premier venu de quelqu'un qui revient — l'invité n'aurait jamais son accueil.
 *
 * Personne ne l'appelait : l'endpoint existait, aucun front ne le touchait. `lastConnectionAt`
 * restait donc nul pour tout le monde, et la garde renvoyait chaque session sur l'accueil.
 */
let connexionEnregistrée = false;

function marquerLaConnexion() {
  if (connexionEnregistrée) return;
  connexionEnregistrée = true;
  // Sans attendre, et sans bruit : c'est une trace, pas une condition d'affichage. Si elle
  // échoue, la personne voit son accueil une fois de plus, ce qui se répare tout seul.
  void http.post('/users/me/connection').catch(() => {
    connexionEnregistrée = false;
  });
}

export function loadCurrentUser(): Promise<CurrentUser> {
  identity ??= http
    .get<{ data: CurrentUser }>('/users/me')
    .then(({ data }) => {
      marquerLaConnexion();
      return data.data;
    })
    .catch((error) => {
      // Un échec ne se retient pas : la garde doit pouvoir redemander plutôt que de refuser
      // tout le reste de la session sur une requête tombée.
      identity = null;
      throw error;
    });
  return identity;
}

/**
 * Oublier ce qu'on a retenu de l'identité.
 *
 * La garde de route lit une promesse mise en cache pour ne pas coûter un aller-retour par
 * navigation. Après l'écran de bienvenue, cette promesse dit encore « il manque un nom » —
 * et renverrait la personne sur l'accueil qu'elle vient de remplir. À appeler dès qu'on
 * change quelque chose que la garde regarde.
 */
export function forgetCurrentUser() {
  identity = null;
}

export function useCurrentUser() {
  const query = useQuery({
    queryKey: userKeys.me,
    queryFn: async (): Promise<CurrentUser> => {
      const { data } = await http.get<{ data: CurrentUser }>('/users/me');
      // Ici aussi : l'écran de bienvenue vit hors de la mise en page, donc la garde ne
      // charge pas l'identité pendant qu'il est affiché. Sans ce second point d'appel,
      // celui qui vient de le remplir le retrouverait à sa prochaine session.
      marquerLaConnexion();
      return data.data;
    },
    // The identity does not change while the tab is open, and the chrome reads it on
    // every route.
    staleTime: Infinity
  });

  /**
   * The interface follows the language stored on the account.
   *
   * Here rather than at startup: nothing renders before Keycloak has answered, but the
   * account — and therefore the preference — arrives one request later. Watching the query
   * covers both the first answer and a change made in the profile dialog.
   */
  watch(
    () => query.data.value?.preferences?.locale,
    (locale) => applyLocale(locale),
    { immediate: true }
  );

  return query;
}
